const isNotEmpty = (input: unknown) => !(typeof input === 'undefined' || input === null);

/**
 * Serialises a flat context record into `URLSearchParams`.
 *
 * - Primitive values are appended as a single query parameter (`key=value`).
 * - Array values are appended as **repeated** query parameters
 *   (`['a', 'b']` → `key=a&key=b`), the RFC-standard multi-value form that
 *   backends read via `getAll`.
 * - `null`/`undefined` values — and `null`/`undefined` array elements — are omitted.
 *
 * Values (and array elements) are expected to be **primitives**. Each is coerced
 * via `String(value)`, so passing a nested object or array-of-objects would emit
 * the unusable `[object Object]`. The public `QueryStringParams` type on
 * `usePlacementData` enforces this at the caller boundary; this helper does not
 * re-validate, so upstream callers must not pass non-primitive elements.
 *
 * Params are sorted by key so the resulting query string is stable across
 * renders (important for SWR cache keys). `URLSearchParams.sort()` is stable for
 * equal keys, so the order of repeated values is preserved.
 */
export const buildSearchParams = (context: Record<string, unknown>): URLSearchParams => {
	const params = new URLSearchParams();

	for (const [key, value] of Object.entries(context)) {
		if (Array.isArray(value)) {
			value.filter(isNotEmpty).forEach((element) => params.append(key, String(element)));
		} else if (isNotEmpty(value)) {
			params.append(key, String(value));
		}
	}

	params.sort();

	return params;
};
